import { Link } from "react-router-dom";
import classNames from "classnames";
import { Icon } from "components";
import styles from "./Button.module.css";

interface ButtonProps extends React.PropsWithChildren {
  color?: string;
  size?: "small";
  filled?: boolean;
  active?: boolean;
  danger?: boolean;
  disabled?: boolean;
  iconStartElement?: any;
  iconStart?: string;
  iconEnd?: string;
  iconFill?: 0 | 1;
  placeContent?: string;
  style?: React.CSSProperties;
  link?: string;
  download?: string;
  onHover?(value: boolean): void;
  onClick?: React.MouseEventHandler;
}

const Button: React.FC<ButtonProps> = ({
  color = "#034F98",
  size,
  danger,
  filled,
  active,
  disabled,
  iconStartElement,
  iconStart,
  iconEnd,
  iconFill = 0,
  placeContent,
  style: styleProp,
  children: childrenProp,
  link,
  download,
  onClick,
  onHover,
}) => {
  const className = classNames(
    styles.container,
    filled && styles.filled,
    active && styles.active,
    danger && styles.danger,
    disabled && styles.disabled,
    size && styles[size]
  );

  const style = {
    placeContent,
    "--text-color": color,
    "--overlay-color": filled ? "black" : color,
    ...(filled && {
      "--fill-color": color,
      "--text-color": "white",
    }),
    ...styleProp,
  };

  const isDisabledLink = link && disabled;
  const disabledLinkProps = isDisabledLink && {
    "aria-disabled": true,
    tabIndex: -1,
  };

  const hoverProps = onHover && {
    onMouseOver: () => onHover(true),
    onMouseOut: () => onHover(false),
    onFocus: () => onHover(true),
    onBlur: () => onHover(false),
  };

  const props = {
    className,
    style,
    ...hoverProps,
    ...disabledLinkProps,
    onClick,
  };

  const children = (
    <>
      {iconStart && <Icon name={iconStart} fill={iconFill} />}
      {iconStartElement && iconStartElement}
      <span className={styles.children}>{childrenProp}</span>
      {iconEnd && <Icon name={iconEnd} fill={iconFill} />}
      <span className={styles.overlay}></span>
    </>
  );

  if (link) {
    const isExternal = link.startsWith("http") || link.startsWith("blob");

    if (isExternal) {
      return (
        <a {...props} href={link} download={download}>
          {children}
        </a>
      );
    } else {
      return (
        <Link {...props} to={link}>
          {children}
        </Link>
      );
    }
  } else {
    return (
      <button {...props} disabled={disabled}>
        {children}
      </button>
    );
  }
};

export type { ButtonProps };
export { Button };
